import type { MonthKey } from '@mist/content/collection'
import manifest from './collection-media.json'

/**
 * Media families for the birthstone collection, the rough-stone photographs,
 * the special editions, and the hero atmosphere loop, generated by
 * scripts/export-collection-media.mjs (the loop by scripts/export-hero-atmosphere.mjs).
 * Public paths follow the /media/ convention used by lib/media.ts.
 */
type Dimensions = { width: number; height: number }
const records = manifest as Record<string, Dimensions>

function family(dir: string, key: string, fallbackExt: 'png' | 'jpg') {
  const dimensions = records[key]
  if (!dimensions) throw new Error(`No media manifest entry for ${key}; run pnpm media:collection`)
  return {
    avif: `/media/${dir}/${key}.avif`,
    webp: `/media/${dir}/${key}.webp`,
    src: `/media/${dir}/${key}.${fallbackExt}`,
    width: dimensions.width,
    height: dimensions.height,
  }
}

export function collectionBottleMedia(image: string) {
  return family('collection', image, 'png')
}

export function editionMedia(image: string) {
  return family('editions', image, 'jpg')
}

/**
 * Rough-stone photograph keys by month (assets/source/stones). June and
 * November photograph the alternate birthstones supplied with the family
 * draft — moonstone and citrine — while the bottle lines stay Pearl and Topaz,
 * so the stone key is looked up by month rather than derived from the gem.
 */
export const STONE_KEYS = {
  january: 'garnet-january',
  february: 'amethyst-february',
  march: 'aquamarine-march',
  april: 'diamond-april',
  may: 'emerald-may',
  june: 'moonstone-june',
  july: 'ruby-july',
  august: 'peridot-august',
  september: 'sapphire-september',
  october: 'opal-october',
  november: 'citrine-november',
  december: 'turquoise-december',
} as const satisfies Record<MonthKey, string>

/** A stone photograph, centred on a transparent square (no enlargement beyond the cut-out). */
export function stoneMedia(key: string) {
  return family('stones', key, 'png')
}

export function stoneMediaForMonth(month: MonthKey) {
  return stoneMedia(STONE_KEYS[month])
}

/**
 * The hero atmosphere loop: the sanctioned 0.00-2.90 s of the hero footage
 * (sapphire cap in smoke, bottle among crystals) played forward then reversed
 * so it never cuts, slowed 1.6x, muted. The poster is the 0.5 s frame and is
 * the complete hero for no-JS, reduced-motion and Save-Data visitors.
 */
export const HERO_ATMOSPHERE = {
  mp4: '/media/hero/hero-atmosphere.mp4',
  webm: '/media/hero/hero-atmosphere.webm',
  poster: family('hero', 'hero-atmosphere-poster', 'jpg'),
} as const
